// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useMemo } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { chartPalette, colors } from '@loa/design-tokens';
import type { RelationshipCashflow, ScenarioOverride } from '../../api/relationshipCashflowApi';

/**
 * The Cashflow Modelling workspace's primary chart
 * (docs/specs/cashflow-modelling-tab-prd.md R10/R11). Three views, selected by
 * the workspace's segmented control:
 *
 * - `cashflow` — a diverging stacked bar per year: income categories above zero,
 *   spending categories AND tax below zero, plus the annual Surplus line. Tax is
 *   always shown as its own outflow segment.
 * - `combined` (default) — the cashflow bars plus the Net worth trajectory on a
 *   second (right) axis, so flows and wealth read together.
 * - `networth` — the Net worth + Surplus line view (mirrors the Overview card).
 *
 * Scenario event overrides render as reference-line annotations at their
 * validFrom year; clicking the chart-area label opens the editor.
 */

export type CashflowChartMode = 'cashflow' | 'combined' | 'networth';

/** Bar dataKey prefix for a spending series (kept distinct from income labels). */
const OUT = 'out:';
/** Row-key prefix for the per-component net-worth series (Individual mode). */
const NW = 'nw:';

/** Colour for a net-worth component line — Liabilities red (it dips below zero). */
function netWorthColor(label: string, index: number): string {
  return label === 'Liabilities' ? colors.errorText : chartPalette[index % chartPalette.length];
}
/** Synthetic spending series carrying the year's total tax (always shown). */
const TAX_KEY = 'out:__tax';
/** Grouped-view series keys: one income total (up), one spending total (down). */
const INCOME_TOTAL = '__incomeTotal';
const SPENDING_TOTAL = '__spendingTotal';

/**
 * Known category labels → series colors. Anchored on the design-token chart
 * palette; the Figma legend's teal / magenta / lime accents (pension
 * drawdown, occupational pension, asset liquidation) have no token yet and
 * stay literal until the design system adds them.
 */
export const ACCENT_TEAL = '#00A5A5';
const ACCENT_MAGENTA = '#C2188C';
const ACCENT_LIME = '#A4C400';

const CATEGORY_COLORS: Record<string, string> = {
  Employment: colors.purplePrimary,
  'Other income': chartPalette[5],
  Rental: chartPalette[2],
  Dividends: chartPalette[1],
  Savings: chartPalette[2],
  'Deposit interest': chartPalette[4],
  'State pension': chartPalette[3],
  'Pension drawdown': ACCENT_TEAL,
  'Pension lump sum': ACCENT_TEAL,
  'Occupational pension': ACCENT_MAGENTA,
  Welfare: colors.warningText,
  Maintenance: colors.grey03,
  'Asset liquidation': ACCENT_LIME,
};

const PALETTE = [...chartPalette, ACCENT_MAGENTA, ACCENT_TEAL, ACCENT_LIME];

/** Muted, token-only palette for spending series so outflows read distinct from income. */
const OUTFLOW_PALETTE = [colors.grey04, colors.grey03, colors.warningText, colors.lilac04];

function seriesColor(label: string, index: number): string {
  return CATEGORY_COLORS[label] ?? PALETTE[index % PALETTE.length];
}

function outflowColor(index: number): string {
  return OUTFLOW_PALETTE[index % OUTFLOW_PALETTE.length];
}

function money(value: number, currency: string | null): string {
  return new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: currency ?? 'EUR',
    maximumFractionDigits: 0,
  }).format(value);
}

/** A compact annotation label for a scenario event override (chart markers). */
export function annotationLabel(o: ScenarioOverride): string {
  const p = o.payload ?? {};
  switch (o.kind) {
    case 'CASHFLOW_ADD':
      return `${p.direction === 'INCOME' ? '+' : '−'} ${p.label ?? 'Scenario item'}`;
    case 'ACCOUNT_ADD':
      return `+ ${p.label ?? p.accountType ?? 'Account'}`;
    case 'ASSET_ADD':
      return `+ ${p.name ?? p.assetType ?? 'Asset'}`;
    case 'DISPOSAL':
      return `Sell ${String(o.figureRef ?? p.targetRef ?? 'item').split(':')[0]}`;
    case 'SWEEP_SURPLUS':
      return 'Sweep surplus';
    case 'SWEEP_RULE':
      return 'Sweep rule';
    default:
      return '';
  }
}

/** Event-override kinds that mark the timeline (dated changes worth annotating). */
const ANNOTATED_KINDS = new Set([
  'CASHFLOW_ADD', 'ACCOUNT_ADD', 'ASSET_ADD', 'DISPOSAL', 'SWEEP_SURPLUS', 'SWEEP_RULE',
]);

interface Props {
  cashflow: RelationshipCashflow;
  mode: CashflowChartMode;
  /**
   * 'grouped' (default) draws one Income bar (up) and one Spending bar (down) —
   * the per-category breakdown stays in the tooltip; 'individual' stacks a bar
   * per income/expenditure category. Ignored in the net-worth view.
   */
  breakdown?: 'grouped' | 'individual';
  /** Scenario event overrides to annotate on the timeline (forecast mode only). */
  overrides?: ScenarioOverride[];
  /** Primary client's birth year — x-axis shows age when known, year otherwise. */
  primaryBirthYear?: number | null;
  onAnnotationClick?: () => void;
}

export function CashflowIncomeChart({
  cashflow,
  mode,
  breakdown = 'grouped',
  overrides,
  primaryBirthYear,
  onAnnotationClick,
}: Props) {
  const currency = cashflow.currency;

  const showFlows = mode !== 'networth';
  const showWealth = mode !== 'cashflow';
  // Vertical annotations (retirement / event markers) bind to whichever axis exists.
  const markerAxisId = showFlows ? 'flows' : 'wealth';

  // Income series = union of income categories across all years, largest total
  // first (stable stacking order with the dominant series at the base).
  const incomeCategories = useMemo(() => {
    const totals = new Map<string, number>();
    for (const row of cashflow.years) {
      for (const [label, value] of Object.entries(row.incomeByCategory ?? {})) {
        totals.set(label, (totals.get(label) ?? 0) + value);
      }
    }
    return [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([label]) => label);
  }, [cashflow.years]);

  // Spending series = union of expenditure categories, largest total first.
  const expenditureCategories = useMemo(() => {
    const totals = new Map<string, number>();
    for (const row of cashflow.years) {
      for (const [label, value] of Object.entries(row.expenditureByCategory ?? {})) {
        totals.set(label, (totals.get(label) ?? 0) + value);
      }
    }
    return [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([label]) => label);
  }, [cashflow.years]);

  // Net-worth components in the server's fixed order (Pensions / Investments /
  // Property / Cash & savings / Other assets / Liabilities), skipping any that are
  // all-zero across the horizon so the chart isn't cluttered with empty lines.
  const netWorthComponents = useMemo(() => {
    const order: string[] = [];
    const nonZero = new Set<string>();
    for (const row of cashflow.years) {
      for (const [label, value] of Object.entries(row.netWorthByComponent ?? {})) {
        if (!order.includes(label)) order.push(label);
        if (value !== 0) nonZero.add(label);
      }
    }
    return order.filter((label) => nonZero.has(label));
  }, [cashflow.years]);

  const axisLabel = (calendarYear: number): number =>
    primaryBirthYear ? calendarYear - primaryBirthYear : calendarYear;

  const rows = useMemo(
    () =>
      cashflow.years.map((y) => {
        const inc = y.incomeByCategory ?? {};
        const exp = y.expenditureByCategory ?? {};
        const incomeSum = incomeCategories.reduce((s, c) => s + (inc[c] ?? 0), 0);
        const spendSum = expenditureCategories.reduce((s, c) => s + (exp[c] ?? 0), 0);
        return {
          x: axisLabel(y.calendarYear),
          calendarYear: y.calendarYear,
          netWorth: y.netWorth,
          surplus: y.surplus,
          netIncome: y.netIncome,
          totalExpenditure: y.totalExpenditure,
          tax: y.tax,
          taxComponents: y.taxByComponent ?? {},
          // Raw per-category maps — the tooltip always shows the full breakdown,
          // even when the bars are grouped.
          __inc: inc,
          __exp: exp,
          __nw: y.netWorthByComponent ?? {},
          // Grouped totals: income up (positive), spending down (negative).
          [INCOME_TOTAL]: incomeSum,
          [SPENDING_TOTAL]: -spendSum,
          // Individual per-category series (income up; spending + tax down).
          ...Object.fromEntries(incomeCategories.map((c) => [c, inc[c] ?? 0])),
          ...Object.fromEntries(expenditureCategories.map((c) => [`${OUT}${c}`, -(exp[c] ?? 0)])),
          // Per-component net worth for the Individual net-worth lines.
          ...Object.fromEntries(
            netWorthComponents.map((c) => [`${NW}${c}`, (y.netWorthByComponent ?? {})[c] ?? 0]),
          ),
          [TAX_KEY]: -(y.tax ?? 0),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cashflow.years, incomeCategories, expenditureCategories, netWorthComponents, primaryBirthYear],
  );

  // The "Retires" life-event marker (design: red dashed line, top label).
  const retirement = useMemo(() => {
    const event = cashflow.events?.find((e) => e.type === 'RETIREMENT');
    if (!event) return null;
    const row = cashflow.years[event.year];
    return row ? axisLabel(row.calendarYear) : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cashflow.events, cashflow.years, primaryBirthYear]);

  // One annotation per override year (dedup by year to avoid label pile-ups).
  const annotations = useMemo(() => {
    const byYear = new Map<number, string>();
    for (const o of overrides ?? []) {
      if (!ANNOTATED_KINDS.has(o.kind) || !o.validFrom) continue;
      const year = Number(o.validFrom.slice(0, 4));
      if (Number.isNaN(year)) continue;
      const x = primaryBirthYear ? year - primaryBirthYear : year;
      const label = annotationLabel(o);
      byYear.set(x, byYear.has(x) ? `${byYear.get(x)} · ${label}` : label);
    }
    return [...byYear.entries()].map(([x, label]) => ({ x, label }));
  }, [overrides, primaryBirthYear]);

  const yTick = (v: number) => money(v, currency).replace(/\.00$/, '');

  return (
    <div data-testid="cashflow-income-chart" className="w-full h-96">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={rows} margin={{ top: 24, right: 16, bottom: 4, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.lilac04} vertical={false} />
          <XAxis
            dataKey="x"
            // The default horizon now runs to client age 100 (~55-80 points) —
            // let recharts skip ticks rather than overlap them.
            minTickGap={16}
            tick={{ fontSize: 11, fill: colors.grey04 }}
            label={
              primaryBirthYear
                ? { value: 'Age', position: 'insideBottomRight', offset: -2, fontSize: 11 }
                : undefined
            }
          />
          {showFlows && (
            <YAxis
              yAxisId="flows"
              orientation="left"
              tick={{ fontSize: 11, fill: colors.grey04 }}
              tickFormatter={yTick}
              width={82}
            />
          )}
          {showWealth && (
            <YAxis
              yAxisId="wealth"
              orientation={showFlows ? 'right' : 'left'}
              tick={{ fontSize: 11, fill: colors.grey04 }}
              tickFormatter={yTick}
              width={82}
            />
          )}
          {showFlows ? (
            <Tooltip
              content={
                <CashflowCompositionTooltip
                  currency={currency}
                  ageAxis={primaryBirthYear != null}
                  combined={mode === 'combined'}
                  breakdown={breakdown}
                  incomeCategories={incomeCategories}
                  expenditureCategories={expenditureCategories}
                  netWorthComponents={netWorthComponents}
                />
              }
            />
          ) : (
            <Tooltip
              formatter={(value, name) => [money(Number(value ?? 0), currency), String(name)]}
              labelFormatter={(label) => (primaryBirthYear ? `Age ${label}` : `Year ${label}`)}
            />
          )}
          <Legend wrapperStyle={{ fontSize: 12 }} />

          {/* ── Cash Flow bars: income up, spending + tax down (diverging stack). ── */}
          {/* Grouped (default): a single Income + single Spending bar. */}
          {showFlows && breakdown === 'grouped' && (
            <Bar yAxisId="flows" dataKey={INCOME_TOTAL} name="Income" stackId="flow" fill={colors.purplePrimary} maxBarSize={22} />
          )}
          {showFlows && breakdown === 'grouped' && (
            <Bar yAxisId="flows" dataKey={SPENDING_TOTAL} name="Spending" stackId="flow" fill={colors.grey04} maxBarSize={22} />
          )}
          {/* Individual: one stacked segment per income / expenditure category. */}
          {showFlows &&
            breakdown === 'individual' &&
            incomeCategories.map((c, i) => (
              <Bar key={c} yAxisId="flows" dataKey={c} name={c} stackId="flow" fill={seriesColor(c, i)} maxBarSize={22} />
            ))}
          {showFlows &&
            breakdown === 'individual' &&
            expenditureCategories.map((c, i) => (
              <Bar
                key={`${OUT}${c}`}
                yAxisId="flows"
                dataKey={`${OUT}${c}`}
                name={c}
                stackId="flow"
                fill={outflowColor(i)}
                maxBarSize={22}
              />
            ))}
          {showFlows && (
            // Tax is always its own outflow segment (0-height on a pre-tax forecast).
            <Bar key={TAX_KEY} yAxisId="flows" dataKey={TAX_KEY} name="Tax" stackId="flow" fill={colors.errorText} maxBarSize={22} />
          )}
          {showFlows && (
            <>
              <Line yAxisId="flows" type="monotone" dataKey="surplus" name="Surplus" stroke={colors.successText} strokeWidth={2} dot={false} />
              <ReferenceLine yAxisId="flows" y={0} stroke={colors.grey04} strokeDasharray="4 4" />
            </>
          )}

          {/* ── Net worth (combined: right axis; networth: sole axis + surplus). ── */}
          {/* Grouped: the single Net worth line. */}
          {showWealth && breakdown === 'grouped' && (
            <Line yAxisId="wealth" type="monotone" dataKey="netWorth" name="Net worth" stroke={colors.purplePrimary} strokeWidth={2} dot={false} />
          )}
          {/* Individual: one line per net-worth component (they sum to the total). */}
          {showWealth &&
            breakdown === 'individual' &&
            netWorthComponents.map((c, i) => (
              <Line
                key={`${NW}${c}`}
                yAxisId="wealth"
                type="monotone"
                dataKey={`${NW}${c}`}
                name={c}
                stroke={netWorthColor(c, i)}
                strokeWidth={2}
                dot={false}
              />
            ))}
          {mode === 'networth' && (
            <>
              <Line yAxisId="wealth" type="monotone" dataKey="surplus" name="Surplus" stroke={ACCENT_TEAL} strokeWidth={2} dot={false} />
              <ReferenceLine yAxisId="wealth" y={0} stroke={colors.errorText} strokeDasharray="4 4" />
            </>
          )}

          {retirement !== null && (
            <ReferenceLine
              yAxisId={markerAxisId}
              x={retirement}
              stroke={colors.errorText}
              strokeDasharray="4 4"
              label={{ value: 'Retires', position: 'top', fontSize: 10, fill: colors.errorText }}
            />
          )}
          {annotations.map((a) => (
            <ReferenceLine
              key={a.x}
              yAxisId={markerAxisId}
              x={a.x}
              stroke={ACCENT_MAGENTA}
              strokeDasharray="4 2"
              label={{
                value: a.label,
                angle: -90,
                position: 'insideTopLeft',
                fontSize: 10,
                fill: ACCENT_MAGENTA,
                onClick: onAnnotationClick,
                style: onAnnotationClick ? { cursor: 'pointer' } : undefined,
              }}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Recharts tooltip payload entry (loosely typed by recharts). */
interface TooltipEntry {
  dataKey?: string | number;
  name?: string | number;
  color?: string;
  value?: number | string;
  payload?: Record<string, unknown>;
}

/**
 * Cash Flow / Combined tooltip, aligned with the Grouped/Individual toggle:
 * in the <b>individual</b> breakdown it lists every non-zero income/expenditure
 * category (plus Tax); in the <b>grouped</b> breakdown it shows the single
 * Income and Spending totals (plus Tax) — matching the bars on screen. Both end
 * with the Surplus and (combined view) the Net worth. Figures are read from the
 * row's raw category maps, so the totals are exact regardless of which bars render.
 */
export function CashflowCompositionTooltip({
  active,
  payload,
  label,
  currency,
  ageAxis,
  combined,
  breakdown = 'grouped',
  incomeCategories = [],
  expenditureCategories = [],
  netWorthComponents = [],
}: {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: number | string;
  currency: string | null;
  ageAxis: boolean;
  combined?: boolean;
  breakdown?: 'grouped' | 'individual';
  incomeCategories?: string[];
  expenditureCategories?: string[];
  netWorthComponents?: string[];
}) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  const row = (payload[0].payload ?? {}) as {
    surplus?: number;
    netWorth?: number;
    tax?: number;
    __inc?: Record<string, number>;
    __exp?: Record<string, number>;
    __nw?: Record<string, number>;
  };
  // Net-worth component rows (combined + individual): one per non-zero component,
  // matching the on-screen lines; they sum to the Net worth total shown below.
  const nw = row.__nw ?? {};
  const netWorthRows =
    combined && breakdown === 'individual'
      ? netWorthComponents
          .filter((c) => (nw[c] ?? 0) !== 0)
          .map((c, i) => ({ label: c, value: nw[c] ?? 0, color: netWorthColor(c, i) }))
      : [];
  const inc = row.__inc ?? {};
  const exp = row.__exp ?? {};
  const tax = row.tax ?? 0;

  // Rows to render as inflows / outflows, matching the on-screen bars: one
  // total each when grouped, one per non-zero category when individual.
  const sum = (m: Record<string, number>) => Object.values(m).reduce((a, b) => a + b, 0);
  const inflowRows =
    breakdown === 'grouped'
      ? sum(inc) !== 0
        ? [{ label: 'Income', value: sum(inc), color: colors.purplePrimary }]
        : []
      : incomeCategories
          .filter((c) => (inc[c] ?? 0) !== 0)
          .map((c, i) => ({ label: c, value: inc[c] ?? 0, color: seriesColor(c, i) }));
  const outflowRows =
    breakdown === 'grouped'
      ? sum(exp) !== 0
        ? [{ label: 'Spending', value: sum(exp), color: colors.grey04 }]
        : []
      : expenditureCategories
          .filter((c) => (exp[c] ?? 0) !== 0)
          .map((c, i) => ({ label: c, value: exp[c] ?? 0, color: outflowColor(i) }));

  return (
    <div
      data-testid="cashflow-composition-tooltip"
      className="bg-white rounded-lg shadow-dropdown p-sm text-body-04 leading-normal"
    >
      <p className="m-0 mb-xs font-medium text-black">{ageAxis ? `Age ${label}` : `Year ${label}`}</p>
      {inflowRows.map((r) => (
        <p key={r.label} className="m-0 text-grey-04">
          <span style={{ color: r.color }}>■</span> {r.label}: {money(r.value, currency)}
        </p>
      ))}
      {outflowRows.map((r) => (
        <p key={r.label} className="m-0 text-grey-04">
          <span style={{ color: r.color }}>■</span> {r.label}: −{money(r.value, currency)}
        </p>
      ))}
      {tax !== 0 && (
        <p className="m-0 text-grey-04">
          <span style={{ color: colors.errorText }}>■</span> Tax: −{money(tax, currency)}
        </p>
      )}
      <p className="m-0 mt-xs text-black font-medium">Surplus: {money(row.surplus ?? 0, currency)}</p>
      {netWorthRows.map((r) => (
        <p key={r.label} className="m-0 text-grey-04">
          <span style={{ color: r.color }}>■</span> {r.label}: {money(r.value, currency)}
        </p>
      ))}
      {combined && (
        <p className="m-0 text-purple-primary font-medium">Net worth: {money(row.netWorth ?? 0, currency)}</p>
      )}
    </div>
  );
}

/**
 * Legacy income-view tooltip retained for the tax breakdown (E1): the stacked
 * gross series, then Total tax + its Income tax / USC / PRSI / DIRT components,
 * Net income, Expenditure and Surplus.
 */
export function IncomeTaxTooltip({
  active,
  payload,
  label,
  currency,
  ageAxis,
}: {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: number | string;
  currency: string | null;
  ageAxis: boolean;
}) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  const row = (payload[0].payload ?? {}) as {
    tax?: number;
    netIncome?: number;
    totalExpenditure?: number;
    surplus?: number;
    taxComponents?: Record<string, number>;
  };
  const series = payload.filter((p) => p.dataKey !== 'netIncome');
  const components = Object.entries(row.taxComponents ?? {});
  return (
    <div
      data-testid="cashflow-tax-tooltip"
      className="bg-white rounded-lg shadow-dropdown p-sm text-body-04 leading-normal"
    >
      <p className="m-0 mb-xs font-medium text-black">
        {ageAxis ? `Age ${label}` : `Year ${label}`}
      </p>
      {series.map((p) => (
        <p key={String(p.dataKey)} className="m-0 text-grey-04">
          <span style={{ color: p.color }}>■</span> {String(p.name)}:{' '}
          {money(Number(p.value ?? 0), currency)}
        </p>
      ))}
      {(row.tax ?? 0) > 0 && (
        <>
          <p className="m-0 mt-xs text-black font-medium">
            Total tax: −{money(row.tax ?? 0, currency)}
          </p>
          {components.map(([name, value]) => (
            <p key={name} className="m-0 pl-sm text-grey-04">
              {name}: −{money(value, currency)}
            </p>
          ))}
          <p className="m-0 mt-xs text-success-text font-medium">
            Net income: {money(row.netIncome ?? 0, currency)}
          </p>
        </>
      )}
      <p className="m-0 text-grey-04">Expenditure: −{money(row.totalExpenditure ?? 0, currency)}</p>
      <p className="m-0 text-black">Surplus: {money(row.surplus ?? 0, currency)}</p>
    </div>
  );
}

export default CashflowIncomeChart;
